import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Tile, TileGrid } from "@/components/common/Tile";
import { Loading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { useSensors } from "@/hooks/useSensors";
import type { SensorSeries } from "@/api/sensors";

const COLORS = [
  "#2563EB", "#DC2626", "#7C3AED", "#059669",
  "#CA8A04", "#0EA5E9", "#DB2777", "#65A30D",
];

const RANGES: { hours: number; label: string }[] = [
  { hours: 6, label: "6h" },
  { hours: 24, label: "24h" },
  { hours: 48, label: "48h" },
  { hours: 24 * 7, label: "7d" },
  { hours: 24 * 30, label: "30d" },
];

function fmtTick(iso: string, hours: number): string {
  const d = new Date(iso);
  if (hours <= 48) {
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function SensorTile({ series, color, hours }: { series: SensorSeries; color: string; hours: number }) {
  const data = series.points.map(p => ({
    x: p.captured_at,
    value: p.value,
  }));
  const last = data.length ? data[data.length - 1].value : null;
  const title = series.unit ? `${series.label} (${series.unit})` : series.label;
  return (
    <Tile
      title={
        <div className="flex items-baseline justify-between gap-md">
          <span>{title}</span>
          {last !== null && (
            <span className="text-base font-semibold text-text tabular-nums">
              {Number.isInteger(last) ? last : last.toFixed(2)}
            </span>
          )}
        </div>
      }
    >
      <div className="h-56 w-full">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-textLight text-sm">
            No readings in range.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="x"
                stroke="#6B7280"
                fontSize={10}
                tickMargin={4}
                minTickGap={32}
                tickFormatter={(v) => fmtTick(v, hours)}
              />
              <YAxis stroke="#6B7280" fontSize={10} width={48} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                labelFormatter={(v) => new Date(v as string).toLocaleString()}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </Tile>
  );
}

export default function SensorSummary() {
  const { deviceId = "" } = useParams();
  const [hours, setHours] = useState<number>(48);
  const { data, isLoading } = useSensors(deviceId, hours);

  if (isLoading) return <Loading />;
  const series = data?.series ?? [];

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-end justify-between gap-md">
        <div>
          <h1 className="text-2xl font-semibold text-text leading-tight">Sensors</h1>
          <p className="text-sm text-textLight">
            Series pulled from heartbeat stats. Empty until the device reports.
          </p>
        </div>
        <div className="inline-flex rounded-md border border-border bg-surface overflow-hidden text-sm">
          {RANGES.map(r => (
            <button
              key={r.hours}
              type="button"
              onClick={() => setHours(r.hours)}
              className={`px-md py-xs ${hours === r.hours ? "bg-brand text-white" : "text-text hover:bg-brand/5"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {series.length === 0 ? (
        <EmptyState>No sensor data in this range.</EmptyState>
      ) : (
        <TileGrid>
          {series.map((s, i) => (
            <div key={s.key} className="col-span-12 md:col-span-6 lg:col-span-4">
              <SensorTile series={s} color={COLORS[i % COLORS.length]} hours={hours} />
            </div>
          ))}
        </TileGrid>
      )}
    </div>
  );
}
